/**
 * Validation types for workflow sample file validation.
 * Maps to backend DTOs in DRIS.Shared/DTOs/WorkflowFileValidationDtos.cs
 */

/**
 * Severity level for validation issues.
 * Maps to ValidationSeverity enum in backend.
 */
export type ValidationSeverity = 'Error' | 'Warning' | 'Info';

/**
 * Maps numeric severity from API to string literal type.
 * Backend sends: Error=0, Warning=1, Info=2
 */
export function mapSeverityFromApi(severity: number): ValidationSeverity {
  switch (severity) {
    case 0:
      return 'Error';
    case 1:
      return 'Warning';
    case 2:
      return 'Info';
    default:
      return 'Info';
  }
}

/**
 * Result of validating all uploaded sample files for a workflow.
 */
export interface WorkflowFileValidationResult {
  workflowId: number;
  isValid: boolean;
  hasWarnings: boolean;
  validatedAt: string;

  // Per-file validation results
  sapValidation?: FileValidationResult;
  tissueValidation?: FileValidationResult;
  soilValidation?: FileValidationResult;

  // Cross-file validation issues
  crossFileIssues: ValidationIssue[];

  // Summary counts
  totalErrors: number;
  totalWarnings: number;
}

/**
 * Validation result for a single file type (SAP, Tissue, or Soil).
 */
export interface FileValidationResult {
  fileType: string; // "Sap", "Tissue", "Soil"
  fileName: string;
  isValid: boolean;
  hasWarnings: boolean;

  // Sample count validation
  expectedCount: number;
  actualCount: number;
  countMatches: boolean;

  // LandRef/LandBlock matching
  landRefMatches: LandRefMatch[];
  unmatchedFileLandRefs: string[]; // LandRefs in file but not in intake lines
  unmatchedIntakeLandBlocks: string[]; // LandBlocks in intake but not in file

  // Duplicate detection
  duplicates: DuplicateSample[];

  // All validation issues for this file
  issues: ValidationIssue[];

  // Parsed samples preview (first N samples for display)
  samplePreviews: ParsedSamplePreview[];
}

/**
 * Represents a match between file LandRef and intake line LandBlock.
 */
export interface LandRefMatch {
  fileLandRef: string;
  intakeLandBlock?: string;
  intakeLineNumber?: number;
  isMatched: boolean;
  fileRowCount: number; // How many rows in file have this LandRef
}

/**
 * Represents a duplicate sample number found in the file.
 */
export interface DuplicateSample {
  sampleNumber: string;
  rowNumbers: number[];
}

/**
 * A single validation issue (error or warning).
 */
export interface ValidationIssue {
  severity: ValidationSeverity;
  code: string; // e.g., "COUNT_MISMATCH", "LANDREF_UNMATCHED"
  message: string;
  fileType?: string; // null for cross-file issues
  rowNumber?: number; // null for file-level issues
  context: Record<string, string>; // Additional context data
}

/**
 * Preview of a parsed sample for display in validation results.
 */
export interface ParsedSamplePreview {
  rowNumber: number;
  sampleNumber: string;
  landRef?: string;
  clientRef?: string;
  dateReceived?: string;
}
